import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { CircularProgressbar, buildStyles } from "react-circular-progressbar";
import "react-circular-progressbar/dist/styles.css";
import { motion, AnimatePresence } from "framer-motion";
import { Line, Pie } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import SubPageLayout from "../../../components/shared/SubPageLayout";
import { apiConfig } from "../../../utils/apiConfig";
import { useAuth } from "../../../contexts/AuthContext";
import Modal from "../../../components/shared/Modal";

import logger from '../../../utils/logger';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
);

const TabButton = ({ active, onClick, emoji, label }) => {
  const buttonClass = active
    ? "border-blue-500 text-blue-600"
    : "border-transparent text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:text-gray-100 hover:border-gray-300";

  return (
    <button
      className={`flex items-center py-4 px-1 border-b-2 font-medium text-sm ${buttonClass}`}
      onClick={onClick}
    >
      <span className="mr-2">{emoji}</span>
      {label}
    </button>
  );
};

const TabNavigation = ({ activeTab, setActiveTab }) => (
  <div className="border-b border-gray-200 dark:border-gray-700 mb-8">
    <nav className="flex space-x-8">
      <TabButton
        active={activeTab === "dashboard"}
        onClick={() => setActiveTab("dashboard")}
        emoji="📊"
        label="Dashboard"
      />
      <TabButton
        active={activeTab === "retirement"}
        onClick={() => setActiveTab("retirement")}
        emoji="🎯"
        label="Retirement Planning"
      />
      <TabButton
        active={activeTab === "tax"}
        onClick={() => setActiveTab("tax")}
        emoji="📋"
        label="Tax Planning"
      />
      <TabButton
        active={activeTab === "investment"}
        onClick={() => setActiveTab("investment")}
        emoji="📈"
        label="Investment Planning"
      />
      <TabButton
        active={activeTab === "more"}
        onClick={() => setActiveTab("more")}
        emoji="•••"
        label="More Categories"
      />
    </nav>
  </div>
);

const StatCard = ({ title, value, subtitle, emoji, textColor }) => (
  <div className="bg-surface-default dark:bg-surface-default p-6 rounded-lg shadow-md dark:shadow-none">
    <div className="flex items-center justify-between mb-4">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
      <span className={`text-2xl ${textColor}`}>{emoji}</span>
    </div>
    <p className="text-3xl font-bold text-gray-900 dark:text-gray-100">{value}</p>
    {subtitle && <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">{subtitle}</p>}
  </div>
);

const GlobalStats = ({ stats }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
    <StatCard
      title="Total Clients"
      value={stats.totalClients}
      subtitle="Using calculator features"
      emoji="👥"
      textColor="text-blue-500"
    />
    <StatCard
      title="Avg. Calculators/Client"
      value={stats.avgCalculatorsPerClient}
      subtitle={`of ${stats.totalCalculators} total`}
      emoji="🧮"
      textColor="text-green-500"
    />
    <StatCard
      title="Full Access Clients"
      value={stats.clientsWithFullAccess}
      subtitle={`${Math.round((stats.clientsWithFullAccess / stats.totalClients) * 100)}% of total`}
      emoji="⭐"
      textColor="text-yellow-500"
    />
    <StatCard
      title="Completion Rate"
      value={`${stats.completionRate}%`}
      subtitle="Average data completion"
      emoji="📊"
      textColor="text-purple-500"
    />
  </div>
);

const OpportunityInsights = ({ topMissingDataPoints, usageData }) => (
  <div className="bg-surface-default dark:bg-surface-default rounded-lg shadow-md dark:shadow-none p-6 mb-8">
    <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-6">
      Growth Opportunities
    </h2>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Top Missing Data Points
        </h3>
        <div className="space-y-4">
          {topMissingDataPoints.map((item, index) => (
            <div
              key={index}
              className="flex items-center justify-between p-3 bg-blue-50 rounded-lg"
            >
              <div>
                <p className="font-medium text-gray-900 dark:text-gray-100">{item.data}</p>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Affects {item.count} clients
                </p>
              </div>
              <div className="text-right">
                <p className="text-lg font-bold text-blue-600">
                  {item.impact}%
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400">Impact</p>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Calculator Usage
        </h3>
        <div className="relative h-64">
          <Pie
            data={usageData}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              plugins: {
                legend: {
                  position: "right",
                  labels: {
                    boxWidth: 12,
                    padding: 15,
                  },
                },
              },
            }}
          />
        </div>
      </div>
    </div>
  </div>
);

const AIAssistant = ({
  chatMessages,
  chatInput,
  setChatInput,
  handleChatSubmit,
}) => (
  <div className="bg-surface-default dark:bg-surface-default rounded-lg shadow-md dark:shadow-none p-6 mb-8">
    <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">AI Assistant</h2>
    <div className="h-64 overflow-y-auto border rounded-lg mb-4">
      <div className="p-4 space-y-4">
        {chatMessages.map((message, index) => (
          <div
            key={index}
            className={`flex ${message.type === "user" ? "justify-end" : "justify-start"}`}
          >
            <div
              className={`max-w-3/4 p-3 rounded-lg ${
                message.type === "user"
                  ? "bg-blue-500 text-white"
                  : "bg-gray-100 text-gray-900 dark:text-gray-100"
              }`}
            >
              {message.content}
            </div>
          </div>
        ))}
      </div>
    </div>
    <form onSubmit={handleChatSubmit}>
      <div className="flex gap-2">
        <input
          type="text"
          value={chatInput}
          onChange={(e) => setChatInput(e.target.value)}
          placeholder="Ask about calculator recommendations..."
          className="flex-1 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
        <button
          type="submit"
          className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
        >
          Send
        </button>
      </div>
    </form>
  </div>
);

const FinancialCalculators = () => {
  const { householdId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [clientData, setClientData] = useState(null);
  const [activeTab, setActiveTab] = useState("dashboard");
  const [chatInput, setChatInput] = useState("");
  const [chatMessages, setChatMessages] = useState([
    {
      type: "bot",
      content:
        "What would you like to calculate today? I can help you find the right calculator for your needs.",
    },
  ]);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [clientSearchQuery, setClientSearchQuery] = useState("");
  const [selectedClient, setSelectedClient] = useState(null);
  const [showAvailableOnly, setShowAvailableOnly] = useState(false);
  const [showDataEntryModal, setShowDataEntryModal] = useState(false);
  const [selectedCalculator, setSelectedCalculator] = useState(null);
  const [isHeaderExpanded, setIsHeaderExpanded] = useState(false);
  const [showCalculators, setShowCalculators] = useState(false);
  const [topMissingDataPoints, setTopMissingDataPoints] = useState([]);
  const { token } = useAuth();

  // Global statistics
  const [globalStats] = useState({
    totalClients: 140,
    avgCalculatorsPerClient: 32,
    clientsWithFullAccess: 28,
    totalCalculators: 46,
    completionRate: 69,
    topMissingDataPoints: [
      { data: "retirement account balances", count: 45, impact: 35 },
      { data: "investment goals", count: 38, impact: 28 },
      { data: "risk preferences", count: 32, impact: 25 },
      { data: "life expectancy", count: 28, impact: 22 },
    ],
  });

  // Chart data for calculator usage
  const [usageData, setUsageData] = useState({
    labels: [
      "Retirement",
      "Tax",
      "Investment",
      "Debt",
      "Income",
      "Education",
      "Real Estate",
      "Insurance",
    ],
    datasets: [
      {
        label: "Calculator Usage by Category",
        data: [85, 72, 68, 65, 60, 55, 50, 45],
        backgroundColor: [
          "#4F46E5",
          "#3B82F6",
          "#06B6D4",
          "#10B981",
          "#84CC16",
          "#EAB308",
          "#F97316",
          "#EF4444",
        ],
      },
    ],
  });

  // Mock clients data for testing
  const [clients] = useState([
    {
      id: 490,
      name: "Smith Household",
      householdId: "490",
      missingData: ["retirement account balances", "investment goals"],
      availableData: ["income", "expenses", "tax filing status"],
    },
    // Add more mock clients as needed
  ]);

  // Determine which workflow we're in based on the URL path
  const isAdvisorWorkflow = location.pathname.includes("/advisor-portal");

  // Generate breadcrumbs based on workflow
  const getBreadcrumbItems = () => {
    if (isAdvisorWorkflow) {
      return [
        { label: "Advisor Portal", path: "/advisor-portal" },
        { label: "Client Database", path: "/advisor-portal/clients" },
        {
          label: "Household Profile",
          path: `/advisor-portal/household-profile/${householdId}`,
        },
        {
          label: "Financial Calculators",
          path: `/advisor-portal/household-profile/${householdId}/calculators`,
        },
      ];
    } else {
      return [
        { label: "Financial Planning", path: "/financial-planning" },
        {
          label: "Financial Calculators",
          path: "/financial-planning/financial-calculators",
        },
        ...(householdId
          ? [
              {
                label: selectedClient ? `${selectedClient.name}` : "Household",
                path: `/advisor-portal/household-profile/${householdId}`,
              },
            ]
          : []),
      ];
    }
  };

  const calculatorCategories = [
    {
      id: "retirement",
      name: "Retirement Planning",
      icon: (
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      ),
      calculators: [
        {
          id: "rmd",
          name: "RMD Calculator",
          description:
            "Calculate the minimum amount you must withdraw from your retirement accounts each year after age 73.",
          requiredData: ["birthdate", "retirement account balances"],
        },
        {
          id: "efficient-distribution",
          name: "Efficient Distribution Calculator",
          description:
            "Determine how to distribute IRA funds over retirement to limit tax liability and reduce impacts of RMDs.",
          requiredData: [
            "retirement account balances",
            "expected income",
            "tax brackets",
          ],
        },
        {
          id: "retirement-savings",
          name: "Retirement Savings Tracker",
          description:
            "Track the progress of your current savings against your retirement goal.",
          requiredData: ["current savings", "retirement goal", "time horizon"],
        },
        {
          id: "retirement-age",
          name: "Retirement Age Optimizer",
          description:
            "Determine the optimal age to retire based on income, expenses, and savings.",
          requiredData: ["current savings", "income", "expenses"],
        },
        {
          id: "annuity-payout",
          name: "Annuity Payout Calculator",
          description:
            "Calculate the monthly or annual payout from a purchased annuity.",
          requiredData: ["annuity balance", "age", "payout options"],
        },
        {
          id: "safe-withdrawal",
          name: "Safe Withdrawal Rate Calculator",
          description:
            "Estimate the sustainable withdrawal rate from your portfolio without running out of money.",
          requiredData: [
            "portfolio balance",
            "retirement duration",
            "investment mix",
          ],
        },
      ],
    },
    {
      id: "tax",
      name: "Tax Planning",
      icon: (
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3.5-2 3.5 2 3.5-2 3.5 2z"
          />
        </svg>
      ),
      calculators: [
        {
          id: "roth-conversion",
          name: "Roth Conversion Calculator",
          description:
            "Evaluate the financial impact of converting a traditional IRA to a Roth IRA, including tax implications.",
          requiredData: [
            "traditional IRA balance",
            "current tax bracket",
            "expected future tax bracket",
          ],
        },
        {
          id: "tax-bracket",
          name: "Tax Bracket Projection",
          description:
            "Predict future tax brackets based on income growth and tax law changes.",
          requiredData: [
            "current income",
            "expected income changes",
            "tax filing status",
          ],
        },
        {
          id: "capital-gains",
          name: "Capital Gains Calculator",
          description: "Assess tax liabilities from selling investments.",
          requiredData: [
            "investment cost basis",
            "current value",
            "holding period",
          ],
        },
        {
          id: "tax-loss-harvesting",
          name: "Tax Loss Harvesting Calculator",
          description:
            "Evaluate the potential tax benefits of selling underperforming investments.",
          requiredData: ["investment losses", "taxable income", "tax bracket"],
        },
        {
          id: "charitable-contribution",
          name: "Charitable Contribution Calculator",
          description:
            "Determine how charitable giving affects your tax burden.",
          requiredData: [
            "donation amount",
            "tax bracket",
            "itemized deductions",
          ],
        },
      ],
    },
    {
      id: "investment",
      name: "Investment Planning",
      icon: (
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
          />
        </svg>
      ),
      calculators: [
        {
          id: "social-security",
          name: "Social Security Breakeven",
          description:
            "Determine when delaying Social Security benefits pays off compared to taking them early.",
          requiredData: ["birthdate", "earnings history", "life expectancy"],
        },
        {
          id: "portfolio-rebalancing",
          name: "Portfolio Rebalancing",
          description:
            "Suggest adjustments to align investments with target asset allocation.",
          requiredData: [
            "current allocation",
            "target allocation",
            "portfolio value",
          ],
        },
        {
          id: "compound-interest",
          name: "Compound Interest Growth",
          description:
            "Model the growth of investments over time with compounding interest.",
          requiredData: [
            "principal",
            "interest rate",
            "time period",
            "contribution frequency",
          ],
        },
        {
          id: "dividend-reinvestment",
          name: "Dividend Reinvestment",
          description:
            "Calculate the long-term impact of reinvesting dividends.",
          requiredData: [
            "investment value",
            "dividend yield",
            "reinvestment period",
          ],
        },
        {
          id: "risk-tolerance",
          name: "Risk Tolerance Assessment",
          description:
            "Provide investment options aligned with your risk tolerance.",
          requiredData: ["age", "investment goals", "risk preferences"],
        },
      ],
    },
    {
      id: "debt",
      name: "Debt Management",
      icon: (
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      ),
      calculators: [
        {
          id: "debt-payoff",
          name: "Debt Payoff Optimizer",
          description:
            "Create a repayment schedule that minimizes interest and shortens debt payoff timelines.",
          requiredData: [
            "debt balances",
            "interest rates",
            "monthly payment capacity",
          ],
        },
        {
          id: "debt-snowball",
          name: "Debt Snowball Calculator",
          description:
            "Plan a repayment schedule using the debt snowball method.",
          requiredData: [
            "debt balances",
            "minimum payments",
            "extra payment amount",
          ],
        },
        {
          id: "debt-avalanche",
          name: "Debt Avalanche Calculator",
          description: "Optimize repayment to minimize interest costs.",
          requiredData: ["debt balances", "interest rates", "minimum payments"],
        },
        {
          id: "credit-score",
          name: "Credit Score Simulator",
          description:
            "Predict the impact of financial decisions on your credit score.",
          requiredData: ["current score", "credit history", "payment history"],
        },
        {
          id: "loan-refinancing",
          name: "Loan Refinancing Comparison",
          description: "Compare the costs and benefits of refinancing loans.",
          requiredData: [
            "current loan terms",
            "new loan options",
            "closing costs",
          ],
        },
      ],
    },
    {
      id: "income",
      name: "Income & Budget",
      icon: (
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      ),
      calculators: [
        {
          id: "net-income",
          name: "Household Net Income",
          description:
            "Calculate your net household income after taxes and deductions.",
          requiredData: ["gross income", "tax filing status", "deductions"],
        },
        {
          id: "expense-tracker",
          name: "Household Expense Tracker",
          description:
            "Calculate monthly and annual expenses to identify areas for savings.",
          requiredData: ["income", "expenses by category", "savings goals"],
        },
        {
          id: "freelance-income",
          name: "Freelance Income Calculator",
          description: "Estimate self-employment taxes and net income.",
          requiredData: ["gross income", "business expenses", "tax deductions"],
        },
        {
          id: "savings-rate",
          name: "Savings Rate Optimizer",
          description:
            "Analyze your savings rate relative to income and expenses.",
          requiredData: ["income", "expenses", "current savings"],
        },
        {
          id: "cash-flow",
          name: "Cash Flow Forecasting",
          description:
            "Predict cash flow based on expected income and expense trends.",
          requiredData: [
            "income sources",
            "recurring expenses",
            "variable expenses",
          ],
        },
      ],
    },
    {
      id: "education",
      name: "Education & Family",
      icon: (
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
          />
        </svg>
      ),
      calculators: [
        {
          id: "college-savings",
          name: "College Savings Calculator",
          description: "Estimate how much to save for your child's education.",
          requiredData: ["child age", "college type", "expected costs"],
        },
        {
          id: "dependent-care",
          name: "Dependent Care Expense",
          description:
            "Project costs of daycare, eldercare, or other dependent expenses.",
          requiredData: ["care type", "duration", "income"],
        },
        {
          id: "parental-leave",
          name: "Parental Leave Income Gap",
          description:
            "Calculate the financial impact of taking parental leave.",
          requiredData: ["current income", "leave duration", "benefits"],
        },
        {
          id: "childcare-credit",
          name: "Childcare Tax Credit",
          description: "Assess how childcare tax credits affect your budget.",
          requiredData: ["childcare expenses", "income", "tax filing status"],
        },
      ],
    },
    {
      id: "real-estate",
      name: "Real Estate",
      icon: (
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
          />
        </svg>
      ),
      calculators: [
        {
          id: "rent-vs-buy",
          name: "Rent vs. Buy Calculator",
          description:
            "Compare the financial implications of renting versus buying a home.",
          requiredData: ["rent cost", "home price", "mortgage terms"],
        },
        {
          id: "mortgage-affordability",
          name: "Mortgage Affordability",
          description:
            "Determine how much house you can afford based on income and debt.",
          requiredData: ["income", "monthly debt", "down payment"],
        },
        {
          id: "home-equity",
          name: "Home Equity Loan",
          description:
            "Calculate the potential borrowing amount against your home's equity.",
          requiredData: ["home value", "mortgage balance", "credit score"],
        },
        {
          id: "property-tax",
          name: "Property Tax Impact",
          description:
            "Assess property tax changes based on home purchase or location.",
          requiredData: ["property value", "location", "tax rates"],
        },
      ],
    },
    {
      id: "insurance",
      name: "Insurance & Risk",
      icon: (
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
          />
        </svg>
      ),
      calculators: [
        {
          id: "life-insurance",
          name: "Life Insurance Needs",
          description: "Estimate the required coverage for life insurance.",
          requiredData: ["income", "dependents", "debt obligations"],
        },
        {
          id: "health-insurance",
          name: "Health Insurance Premium Optimizer",
          description:
            "Compare potential costs of different health insurance plans.",
          requiredData: ["health status", "expected usage", "plan options"],
        },
        {
          id: "long-term-care",
          name: "Long-Term Care Expense",
          description: "Project costs for long-term care and insurance needs.",
          requiredData: ["age", "health status", "coverage options"],
        },
        {
          id: "umbrella-insurance",
          name: "Umbrella Insurance Coverage",
          description:
            "Assess whether additional liability coverage is necessary.",
          requiredData: ["assets", "current coverage", "risk factors"],
        },
      ],
    },
    {
      id: "specialized",
      name: "Specialized Calculators",
      icon: (
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"
          />
        </svg>
      ),
      calculators: [
        {
          id: "inheritance-tax",
          name: "Inheritance Tax Impact",
          description:
            "Estimate the tax implications of receiving or leaving an inheritance.",
          requiredData: [
            "inheritance amount",
            "state of residence",
            "relationship to deceased",
          ],
        },
        {
          id: "esg-portfolio",
          name: "ESG Portfolio Impact",
          description:
            "Analyze the financial and ethical impacts of sustainable investing.",
          requiredData: [
            "investment amount",
            "ESG preferences",
            "risk tolerance",
          ],
        },
        {
          id: "hsa-growth",
          name: "HSA Growth Calculator",
          description: "Predict the long-term value of HSA contributions.",
          requiredData: [
            "contribution amount",
            "investment options",
            "time horizon",
          ],
        },
        {
          id: "early-retirement",
          name: "Early Retirement Feasibility",
          description:
            "Evaluate if you can afford to retire earlier than planned.",
          requiredData: [
            "current savings",
            "expenses",
            "desired retirement age",
          ],
        },
        {
          id: "business-startup",
          name: "Business Startup Cost",
          description: "Estimate capital needed to start a business.",
          requiredData: ["industry type", "location", "startup expenses"],
        },
        {
          id: "net-worth",
          name: "Net Worth Tracker",
          description:
            "Measure your financial health by tracking assets and liabilities over time.",
          requiredData: ["assets", "liabilities", "tracking period"],
        },
        {
          id: "crypto-calculator",
          name: "Crypto Investment Risk/Reward",
          description:
            "Assess the potential returns and risks of cryptocurrency investments.",
          requiredData: ["investment amount", "crypto type", "risk tolerance"],
        },
        {
          id: "inflation-impact",
          name: "Inflation Impact Estimator",
          description:
            "Predict the effect of inflation on future purchasing power.",
          requiredData: ["current amount", "time period", "inflation rate"],
        },
      ],
    },
  ];

  const fetchClientData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Validate householdId
      if (!householdId || isNaN(parseInt(householdId))) {
        logger.warn("Invalid householdId:", householdId);
        setError("Invalid household ID");
        return;
      }

      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("Authentication token not found");
      }

      const response = await apiConfig.api.get(
        `/advisor-portal/client-database/household-profile/${parseInt(householdId)}/calculators`,
      );

      if (response.data) {
        setClientData(response.data);
        setUsageData(
          response.data.usageData || {
            totalCalculations: 0,
            mostUsedCalculator: null,
            averageTimeSpent: 0,
          },
        );
        setTopMissingDataPoints(response.data.missingDataPoints || []);
      }
    } catch (error) {
      logger.error("Error fetching client data:", error);
      setError(
        error.response?.data?.message ||
          error.message ||
          "Failed to fetch client data",
      );
    } finally {
      setLoading(false);
    }
  }, [householdId]);

  useEffect(() => {
    fetchClientData();
  }, [householdId, token, navigate]);

  // Calculate calculator availability for selected client
  const getClientCalculatorStats = (client) => {
    if (!client) return null;

    const missingData = client.missingData || [];
    const availableData = client.availableData || [];

    return {
      available: 32,
      total: 46,
      percentage: Math.round((32 / 46) * 100),
      calculatorStatus: {},
      prioritizedMissingData: missingData.map((data, index) => ({
        data,
        count: 5 - index,
        impact: Math.round(((5 - index) / 46) * 100),
      })),
    };
  };

  const clientStats = selectedClient
    ? getClientCalculatorStats(selectedClient)
    : null;

  const handleClientSelect = (client) => {
    if (!client) return;

    setSelectedClient(client);
    setChatMessages((prev) => [
      ...prev,
      {
        type: "bot",
        content: `I've analyzed ${client.name}'s profile. Let me help you find the best calculators to use and identify what information would be most valuable to collect.`,
      },
    ]);
  };

  const filteredCalculators = calculatorCategories
    .filter(
      (category) =>
        selectedCategory === "all" || category.id === selectedCategory,
    )
    .map((category) => ({
      ...category,
      calculators: category.calculators.filter(
        (calc) =>
          calc.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          calc.description.toLowerCase().includes(searchQuery.toLowerCase()),
      ),
    }))
    .filter((category) => category.calculators.length > 0);

  const handleChatSubmit = (e) => {
    e.preventDefault();
    if (!chatInput.trim()) return;

    // Add user message
    setChatMessages((prev) => [...prev, { type: "user", content: chatInput }]);

    // Enhanced AI response based on selected client and context
    setTimeout(() => {
      let response = "";

      if (selectedClient) {
        if (
          chatInput.toLowerCase().includes("available") ||
          chatInput.toLowerCase().includes("ready")
        ) {
          const stats = getClientCalculatorStats(selectedClient);
          response = `${selectedClient.name} has ${stats.available} calculators ready to use out of ${stats.total} total calculators. Would you like me to show you the available calculators?`;
        } else if (
          chatInput.toLowerCase().includes("missing") ||
          chatInput.toLowerCase().includes("need")
        ) {
          const stats = getClientCalculatorStats(selectedClient);
          const topMissing = stats.prioritizedMissingData[0];
          response = `The most impactful missing information for ${selectedClient.name} is "${topMissing.data}" - adding this would unlock ${topMissing.count} new calculators (${topMissing.impact}% impact). Would you like to see the full list of missing information?`;
        } else {
          response =
            "Based on your question, I recommend the following calculators: ";
          if (chatInput.toLowerCase().includes("retirement")) {
            response += "RMD Calculator, Retirement Savings Tracker";
          } else if (chatInput.toLowerCase().includes("tax")) {
            response += "Roth Conversion Calculator, Tax Bracket Projection";
          } else if (chatInput.toLowerCase().includes("investment")) {
            response +=
              "Portfolio Rebalancing Calculator, Social Security Breakeven Calculator";
          }
        }
      } else {
        response =
          "Please select a client first, and I can help you find the most relevant calculators for their situation.";
      }

      setChatMessages((prev) => [...prev, { type: "bot", content: response }]);
    }, 1000);

    setChatInput("");
  };

  // Back button handler
  const handleBackNavigation = () => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/");
      return;
    }

    if (isAdvisorWorkflow && householdId) {
      navigate(`/advisor-portal/household-profile/${householdId}`);
    } else {
      navigate("/financial-planning");
    }
  };

  // Update the calculator navigation when launching a calculator
  const handleCalculatorLaunch = (calculator) => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/");
      return;
    }

    // Special handling for RMD calculator
    if (calculator.id === "rmd") {
      if (isAdvisorWorkflow && householdId) {
        navigate(
          `/advisor-portal/client-database/household-profile/${householdId}/calculators/rmd`,
        );
      } else {
        navigate("/financial-planning/calculators/rmd");
      }
      return;
    }

    // Determine the base path based on the workflow
    const basePath = isAdvisorWorkflow
      ? `/advisor-portal/client-database/household-profile/${householdId}/calculators`
      : `/financial-planning/financial-calculators${householdId ? `/${householdId}` : ""}`;

    // Navigate to the calculator
    navigate(`${basePath}/${calculator.id}`);
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <SubPageLayout
      breadcrumbItems={getBreadcrumbItems()}
      title={
        loading
          ? "Loading..."
          : error
            ? "Error"
            : householdId && selectedClient
              ? `${selectedClient.name} Calculators`
              : "Financial Calculators"
      }
      subtitle="Comprehensive financial planning tools and insights"
    >
      <div className="max-w-7xl mx-auto">
        {!showCalculators ? (
          // Only show dashboard view in non-household context
          !householdId ? ( // Dashboard View
            <>
              {/* View All Calculators Button */}
              <div className="flex justify-end mb-6">
                <button
                  onClick={() => setShowCalculators(true)}
                  className="inline-flex items-center px-6 py-3 bg-[#202EC6] text-white rounded-lg hover:bg-[#1a24a1] text-base font-medium"
                >
                  View All Calculators
                  <svg
                    className="w-5 h-5 ml-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </button>
              </div>
              {/* Global Stats */}
              <GlobalStats stats={globalStats} />
              {/* Growth Opportunities and Calculator Usage */}
              <OpportunityInsights
                topMissingDataPoints={globalStats.topMissingDataPoints}
                usageData={usageData}
              />
              {/* AI Assistant */}
              <AIAssistant
                chatMessages={chatMessages}
                chatInput={chatInput}
                setChatInput={setChatInput}
                handleChatSubmit={handleChatSubmit}
              />
            </> // If household ID is present, show calculators view immediately
          ) : (
            setShowCalculators(true)
          )
        ) : (
          // Calculators View
          <div className="flex flex-col">
            {/* Back Button */}
            <button
              onClick={handleBackNavigation}
              className="mb-6 inline-flex items-center px-4 py-2 bg-gray-100 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200"
            >
              <svg
                className="w-5 h-5 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
              {isAdvisorWorkflow && householdId
                ? "Back to Household"
                : "Back to Dashboard"}
            </button>
            {/* Main Content */}
            <div className="flex gap-6">
              {/* Left Column - Only show client selection in non-household context */}
              {!householdId && (
                <div className="w-80 flex flex-col gap-6">
                  {/* Client Selection */}
                  <div className="bg-surface-default dark:bg-surface-default rounded-lg shadow">
                    <div className="p-4 border-b">
                      <h2 className="font-medium text-gray-900 dark:text-gray-100">
                        Client Selection
                      </h2>
                    </div>
                    <div className="p-4">
                      <div className="relative mb-4">
                        <input
                          type="text"
                          placeholder="Search clients..."
                          value={clientSearchQuery}
                          onChange={(e) => setClientSearchQuery(e.target.value)}
                          className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#202EC6] focus:border-[#202EC6]"
                        />
                        <svg
                          className="absolute left-3 top-2.5 w-5 h-5 text-gray-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                          />
                        </svg>
                      </div>
                      <div className="space-y-3">
                        {clients
                          .filter((client) =>
                            client.name
                              .toLowerCase()
                              .includes(clientSearchQuery.toLowerCase()),
                          )
                          .map((client) => {
                            const stats = getClientCalculatorStats(client);
                            return (
                              <motion.button
                                key={client.id}
                                onClick={() => handleClientSelect(client)}
                                className={`w-full rounded-lg border transition-all ${
                                  selectedClient?.id === client.id
                                    ? "border-[#202EC6] bg-blue-50"
                                    : "border-gray-200 dark:border-gray-700 hover:border-[#202EC6] hover:bg-blue-50"
                                }`}
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                              >
                                <div className="p-4">
                                  <div className="flex justify-between items-start">
                                    <div>
                                      <div className="font-medium text-gray-900 dark:text-gray-100 text-left">
                                        {client.name}
                                      </div>
                                      <div className="mt-1 flex items-center gap-2">
                                        <div className="text-sm text-gray-600 dark:text-gray-400">
                                          {stats.available} / {stats.total}{" "}
                                          Calculators
                                        </div>
                                        <div className="h-1.5 w-20 bg-gray-200 rounded-full overflow-hidden">
                                          <div
                                            className="h-full bg-[#202EC6]"
                                            style={{
                                              width: `${stats.percentage}%`,
                                            }}
                                          />
                                        </div>
                                      </div>
                                    </div>
                                    {client.missingData.length > 0 && (
                                      <div className="flex items-center">
                                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                          {client.missingData.length} Missing
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                  {selectedClient?.id === client.id && (
                                    <div className="mt-3 pt-3 border-t">
                                      <div className="text-sm text-gray-600 dark:text-gray-400">
                                        <span className="font-medium">
                                          Top missing data:
                                        </span>{" "}
                                        {client.missingData
                                          .slice(0, 2)
                                          .join(", ")}
                                        {client.missingData.length > 2 &&
                                          " ..."}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </motion.button>
                            );
                          })}
                      </div>
                    </div>
                  </div>

                  {/* Calculator Search and Filter */}
                  <div className="bg-surface-default dark:bg-surface-default rounded-lg shadow p-4">
                    <div className="space-y-4">
                      <div className="relative">
                        <input
                          type="text"
                          placeholder="Search calculators..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#202EC6] focus:border-[#202EC6]"
                        />
                        <svg
                          className="absolute left-3 top-2.5 w-5 h-5 text-gray-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                          />
                        </svg>
                      </div>

                      {selectedClient && (
                        <div className="flex items-center justify-between py-2">
                          <span className="text-sm text-gray-600 dark:text-gray-400">
                            Show Available Only
                          </span>
                          <button
                            onClick={() =>
                              setShowAvailableOnly(!showAvailableOnly)
                            }
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#202EC6] focus:ring-offset-2 ${
                              showAvailableOnly ? "bg-[#202EC6]" : "bg-gray-200"
                            }`}
                          >
                            <span
                              className={`inline-block h-4 w-4 transform rounded-full bg-surface-default dark:bg-surface-default transition-transform ${
                                showAvailableOnly
                                  ? "translate-x-6"
                                  : "translate-x-1"
                              }`}
                            />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Categories */}
                  <div className="bg-surface-default dark:bg-surface-default rounded-lg shadow overflow-hidden">
                    <div className="p-4 border-b">
                      <h2 className="font-medium text-gray-900 dark:text-gray-100">Categories</h2>
                    </div>
                    <div className="p-2">
                      <button
                        onClick={() => setSelectedCategory("all")}
                        className={`w-full px-4 py-2 rounded-lg text-left mb-1 ${
                          selectedCategory === "all"
                            ? "bg-[#202EC6] text-white hover:bg-[#1a24a1]"
                            : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
                        }`}
                      >
                        All Calculators
                      </button>
                      {calculatorCategories.map((category) => (
                        <button
                          key={category.id}
                          onClick={() => setSelectedCategory(category.id)}
                          className={`w-full px-4 py-2 rounded-lg text-left mb-1 flex items-center ${
                            selectedCategory === category.id
                              ? "bg-[#202EC6] text-white hover:bg-[#1a24a1]"
                              : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
                          }`}
                        >
                          <span
                            className={`mr-3 ${selectedCategory === category.id ? "text-white" : "text-gray-700 dark:text-gray-300"}`}
                          >
                            {category.icon}
                          </span>
                          {category.name}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Right Column - Content */}
              <div
                className={`flex-1 flex flex-col gap-6 ${!householdId ? "" : "w-full"}`}
              >
                {/* Client Stats */}
                {selectedClient && clientStats && (
                  <div className="bg-surface-default dark:bg-surface-default rounded-lg shadow">
                    <div className="p-6 border-b">
                      <div className="flex justify-between items-center">
                        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                          {selectedClient.name} Calculator Availability
                        </h2>
                        <div className="flex items-center gap-4">
                          <div className="text-sm text-gray-500 dark:text-gray-500">
                            Available Calculators
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="text-3xl font-bold text-[#202EC6]">
                              {clientStats.available}
                            </div>
                            <div className="text-lg text-gray-400">
                              / {clientStats.total}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Progress Bar */}
                      <div className="mt-4">
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-[#202EC6] h-2 rounded-full"
                            style={{ width: `${clientStats.percentage}%` }}
                          />
                        </div>
                      </div>

                      {/* Missing Data Impact */}
                      {clientStats.prioritizedMissingData.length > 0 && (
                        <div className="mt-6">
                          <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3">
                            Recommended Data to Collect
                          </h3>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {clientStats.prioritizedMissingData
                              .slice(0, 4)
                              .map(({ data, count, impact }) => (
                                <div
                                  key={data}
                                  className="flex items-center p-3 border rounded-lg bg-blue-50"
                                >
                                  <div className="mr-3">
                                    <svg
                                      className="w-8 h-8 text-[#202EC6]"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M13 10V3L4 14h7v7l9-11h-7z"
                                      />
                                    </svg>
                                  </div>
                                  <div>
                                    <div className="font-medium text-gray-900 dark:text-gray-100">
                                      {data}
                                    </div>
                                    <div className="text-sm text-gray-600 dark:text-gray-400">
                                      Unlocks {count} calculators ({impact}%
                                      impact)
                                    </div>
                                  </div>
                                </div>
                              ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Calculator Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {filteredCalculators.map((category) => (
                    <div key={category.id} className="space-y-4">
                      <div className="flex items-center gap-3 px-1">
                        <span className="p-2 rounded-lg bg-blue-50 text-[#202EC6]">
                          {category.icon}
                        </span>
                        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                          {category.name}
                        </h3>
                      </div>
                      <div className="space-y-4">
                        {category.calculators
                          .filter((calc) => {
                            if (!selectedClient || !showAvailableOnly)
                              return true;
                            const status =
                              clientStats?.calculatorStatus[calc.id];
                            return status?.available;
                          })
                          .map((calculator) => {
                            const status =
                              selectedClient &&
                              clientStats?.calculatorStatus[calculator.id];
                            const isAvailable =
                              !selectedClient || status?.available;

                            return (
                              <motion.div
                                key={calculator.id}
                                className={`p-6 bg-surface-default dark:bg-surface-default rounded-lg shadow-sm border-2 transition-all ${
                                  selectedClient
                                    ? isAvailable
                                      ? "border-green-200 hover:border-green-300"
                                      : "border-red-100 opacity-75"
                                    : "border-gray-100 dark:border-gray-800 hover:border-[#202EC6]"
                                }`}
                                whileHover={{ scale: 1.01 }}
                                whileTap={{ scale: 0.99 }}
                              >
                                <div className="flex justify-between items-start mb-3">
                                  <div>
                                    <h4 className="font-medium text-gray-900 dark:text-gray-100">
                                      {calculator.name}
                                    </h4>
                                    <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                                      {calculator.description}
                                    </p>
                                  </div>
                                  {selectedClient && (
                                    <div
                                      className={`px-2 py-1 rounded-full text-xs font-medium ${
                                        isAvailable
                                          ? "bg-green-100 text-green-800"
                                          : "bg-red-100 text-red-800"
                                      }`}
                                    >
                                      {isAvailable ? "Ready" : "Missing Data"}
                                    </div>
                                  )}
                                </div>

                                <div className="flex items-center justify-between">
                                  <div className="flex flex-wrap gap-2">
                                    {calculator.requiredData.map((data) => (
                                      <span
                                        key={data}
                                        className={`inline-flex items-center px-2 py-1 rounded-full text-xs ${
                                          selectedClient
                                            ? status?.missingData.includes(data)
                                              ? "bg-red-50 text-red-700"
                                              : "bg-green-50 text-green-700"
                                            : "bg-gray-100 text-gray-700 dark:text-gray-300"
                                        }`}
                                      >
                                        {data}
                                      </span>
                                    ))}
                                  </div>
                                  <button
                                    onClick={() => {
                                      if (isAvailable) {
                                        handleCalculatorLaunch(calculator);
                                      } else {
                                        setSelectedCalculator(calculator);
                                        setShowDataEntryModal(true);
                                      }
                                    }}
                                    className={`ml-4 px-4 py-2 rounded-lg text-sm ${
                                      isAvailable
                                        ? "bg-[#202EC6] text-white hover:bg-[#1a24a1]"
                                        : "bg-surface-default dark:bg-surface-default text-[#202EC6] border border-[#202EC6] hover:bg-blue-50"
                                    }`}
                                  >
                                    {isAvailable
                                      ? "Launch Calculator"
                                      : "Add Missing Data"}
                                  </button>
                                </div>
                              </motion.div>
                            );
                          })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      {/* Data Entry Modal */}
      {showDataEntryModal && selectedCalculator && (
        <Modal
          isOpen={showDataEntryModal}
          onClose={() => setShowDataEntryModal(false)}
          maxWidth="2xl"
        >
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                Add Missing Data for {selectedCalculator.name}
              </h3>
              <button
                onClick={() => setShowDataEntryModal(false)}
                className="text-gray-400 hover:text-gray-500 dark:text-gray-500 dark:hover:text-gray-400"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              {selectedCalculator.requiredData
                .filter((data) => {
                  const calcStatus =
                    clientStats?.calculatorStatus[selectedCalculator.id];
                  return calcStatus?.missingData.includes(data);
                })
                .map((data) => (
                  <div key={data} className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      {data}
                    </label>
                    <input
                      type="text"
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-[#202EC6] focus:border-[#202EC6] bg-white dark:bg-surface-raised text-gray-900 dark:text-gray-100"
                      placeholder={`Enter ${data.toLowerCase()}`}
                    />
                  </div>
                ))}
            </div>
          </div>
          <div className="p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-surface-raised flex justify-end gap-4">
            <button
              onClick={() => setShowDataEntryModal(false)}
              className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                // Handle data saving
                setShowDataEntryModal(false);
              }}
              className="px-4 py-2 text-sm bg-[#202EC6] text-white rounded-lg hover:bg-opacity-90"
            >
              Save Data
            </button>
          </div>
        </Modal>
      )}
    </SubPageLayout>
  );
};

export default FinancialCalculators;
